import { Component, inject, input, model, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';

@Component({
  selector: 'app-quote-revision-item-bulk-delete-dialog',
  imports: [ButtonModule, DialogModule],
  templateUrl: './quote-revision-item-bulk-delete-dialog.component.html',
})
export class QuoteRevisionItemBulkDeleteDialogComponent {
  private revisionItemService = inject(QuoteRevisionItemService);
  private messageService = inject(MessageService);

  visible = model(false);
  items = input<QuoteRevisionItem[] | undefined>();
  selectedPersistentIds = input.required<ReadonlySet<string>>();
  deleted = output<void>();

  deleting = false;

  get selectedDeleteRoots(): QuoteRevisionItem[] {
    return this.getSelectedDeleteRoots(this.items());
  }

  get selectedCount(): number {
    return this.selectedPersistentIds().size;
  }

  get nestedCount(): number {
    return this.countUnselectedDescendants(this.selectedDeleteRoots);
  }

  get totalCount(): number {
    return this.selectedDeleteRoots.reduce(
      (sum, root) => sum + 1 + this.countDescendants(root),
      0,
    );
  }

  close(): void {
    if (this.deleting) {
      return;
    }
    this.visible.set(false);
  }

  /**
   * Selected items that have no selected ancestor. Only these should be sent to
   * delete — the backend cascades to children.
   */
  getSelectedDeleteRoots(
    items: QuoteRevisionItem[] | undefined,
  ): QuoteRevisionItem[] {
    if (!items?.length) {
      return [];
    }

    const selected = this.selectedPersistentIds();
    const roots: QuoteRevisionItem[] = [];

    const visit = (
      items: QuoteRevisionItem[],
      ancestorSelected: boolean,
    ): void => {
      for (const item of items) {
        const isSelected = selected.has(item.persistent_id);
        if (isSelected && !ancestorSelected) {
          roots.push(item);
        }
        const children = item.children ?? [];
        if (children.length > 0) {
          visit(children, ancestorSelected || isSelected);
        }
      }
    };

    visit(items, false);
    return roots;
  }

  async deleteSelected(): Promise<void> {
    const roots = this.selectedDeleteRoots;
    if (roots.length === 0 || this.deleting) {
      return;
    }

    // The backend cascades each root to its descendants.
    const deletedItemCount = this.totalCount;

    this.deleting = true;
    try {
      await this.revisionItemService.bulkDelete(
        roots.map((root) => root.persistent_id),
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Bulk delete complete',
        detail: `Deleted ${deletedItemCount} item(s).`,
      });
      this.deleted.emit();
      this.visible.set(false);
    } finally {
      this.deleting = false;
    }
  }

  private countUnselectedDescendants(itemRoots: QuoteRevisionItem[]): number {
    let count = 0;
    const selected = this.selectedPersistentIds();

    const visit = (item: QuoteRevisionItem): void => {
      for (const child of item.children ?? []) {
        if (!selected.has(child.persistent_id)) {
          count++;
        }
        visit(child);
      }
    };

    for (const root of itemRoots) {
      visit(root);
    }

    return count;
  }

  private countDescendants(item: QuoteRevisionItem): number {
    const children = item.children ?? [];
    let count = 0;
    for (const child of children) {
      count += 1 + this.countDescendants(child);
    }
    return count;
  }
}
